/**
 * useDrawingInteraction - Hook that integrates DrawingToolService with
 * the OpenLayers map and Redux store.
 *
 * Manages the lifecycle of drawing interactions, syncs state between
 * the service and Redux, and handles text annotation placement.
 *
 * Validates: Requirements 39.1, 39.2, 39.4, 39.5, 39.6, 40.1, 40.2, 40.3, 40.5, 40.6
 */
import { useEffect, useCallback, useRef } from 'react';
import type Map from 'ol/Map';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  addDrawing,
  updateDrawing,
  deleteDrawing,
  openTextDialog,
  openTextEditDialog,
  setActiveTool,
  clearAllDrawings as clearAllDrawingsAction,
} from '../store/slices/drawingSlice';
import { stopDigitization } from '../store/slices/digitizationSlice';
import { setMeasurementMode } from '../store/slices/measurementSlice';
import { deactivateNodeEditing, deactivateSplitTool } from '../store/slices/geometryEditingSlice';
import { drawingToolService } from '../services/DrawingToolService';
import type { Drawing, TextStyle } from '../types/drawing';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDrawingInteraction(map: Map | null) {
  const dispatch = useAppDispatch();
  const { activeTool, drawings, drawingStyle, textStyle, savedAsLayer } = useAppSelector(
    (state) => state.drawing
  );
  const digitizationMode = useAppSelector((state) => state.digitization.mode);
  const measurementMode = useAppSelector((state) => state.measurement.activeMode);
  const isSplitToolActive = useAppSelector((state) => state.geometryEditing.isSplitToolActive);
  const isEditingGeometry = useAppSelector((state) => state.geometryEditing.isEditingGeometry);
  const initializedRef = useRef(false);
  const drawingsRef = useRef(drawings);
  drawingsRef.current = drawings;

  // Activating a drawing tool is a centralized transition away from other map modes.
  useEffect(() => {
    if (activeTool === null) return;
    dispatch(stopDigitization());
    dispatch(setMeasurementMode(null));
    dispatch(deactivateSplitTool());
    dispatch(deactivateNodeEditing());
  }, [activeTool, dispatch]);

  // Initialize service when the live map is available and restore Redux drawings.
  useEffect(() => {
    if (!map) return;

    drawingToolService.initialize(map);
    initializedRef.current = true;
    drawingToolService.loadDrawings(drawingsRef.current);

    drawingToolService.setOnDrawingAdded((drawing: Drawing) => {
      dispatch(addDrawing(drawing));
    });
    drawingToolService.setOnDrawingChanged((drawing: Drawing) => {
      const { id, ...updates } = drawing;
      dispatch(updateDrawing({ id, updates }));
    });
    drawingToolService.setOnTextPlacement((coords: [number, number]) => {
      dispatch(openTextDialog(coords));
    });

    return () => {
      drawingToolService.setOnDrawingAdded(null);
      drawingToolService.setOnDrawingChanged(null);
      drawingToolService.setOnTextPlacement(null);
      drawingToolService.dispose();
      initializedRef.current = false;
    };
  }, [map, dispatch]);

  // Redux is authoritative for drawing data, including delete/clear and remount restore.
  useEffect(() => {
    if (!initializedRef.current) return;
    drawingToolService.loadDrawings(drawings);
  }, [drawings]);

  // Sync active tool with service only while drawing owns map interaction.
  useEffect(() => {
    if (!initializedRef.current) return;

    const ownsMap =
      digitizationMode === 'idle' &&
      measurementMode === null &&
      !isSplitToolActive &&
      !isEditingGeometry;

    if (!ownsMap) {
      drawingToolService.deactivateInteractions();
    } else if (activeTool === null && drawings.length > 0) {
      drawingToolService.enableModify();
    } else if (activeTool !== null) {
      drawingToolService.activateTool(activeTool);
    } else {
      drawingToolService.deactivateInteractions();
    }
  }, [
    activeTool,
    drawings.length,
    digitizationMode,
    measurementMode,
    isSplitToolActive,
    isEditingGeometry,
  ]);

  // Sync drawing style with service
  useEffect(() => {
    if (!initializedRef.current) return;
    drawingToolService.setDrawingStyle(drawingStyle);
  }, [drawingStyle]);

  // Sync text style with service
  useEffect(() => {
    if (!initializedRef.current) return;
    drawingToolService.setTextStyle(textStyle);
  }, [textStyle]);

  // Handle save as layer
  useEffect(() => {
    if (savedAsLayer && drawings.length > 0) {
      // The actual layer creation is handled by the component that
      // listens for the savedAsLayer flag
    }
  }, [savedAsLayer, drawings]);

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * Add a text annotation at the specified coordinates.
   */
  const addTextAnnotation = useCallback(
    (coords: [number, number], text: string, style: TextStyle) => {
      if (!initializedRef.current) return;
      const drawing = drawingToolService.addTextAnnotation(coords, text, style);
      dispatch(addDrawing(drawing));
    },
    [dispatch]
  );

  /**
   * Update an existing text annotation.
   */
  const updateTextAnnotation = useCallback(
    (drawingId: string, text: string, style: TextStyle) => {
      if (!initializedRef.current) return;
      drawingToolService.updateTextAnnotation(drawingId, text, style);
      dispatch(
        updateDrawing({
          id: drawingId,
          updates: { text, textStyle: style },
        })
      );
    },
    [dispatch]
  );

  /**
   * Delete a drawing by ID.
   */
  const removeDrawing = useCallback(
    (drawingId: string) => {
      if (!initializedRef.current) return;
      drawingToolService.deleteDrawingById(drawingId);
      dispatch(deleteDrawing(drawingId));
    },
    [dispatch]
  );

  /**
   * Clear all drawings from the map.
   */
  const clearAll = useCallback(() => {
    if (!initializedRef.current) return;
    drawingToolService.clearAll();
    dispatch(clearAllDrawingsAction());
  }, [dispatch]);

  /**
   * Get all drawings as GeoJSON for saving as a vector layer.
   */
  const getDrawingsAsGeoJSON = useCallback(() => {
    if (!initializedRef.current) return null;
    return drawingToolService.getDrawingsAsGeoJSON();
  }, []);

  /**
   * Open edit dialog for a text annotation.
   */
  const editTextAnnotation = useCallback(
    (drawingId: string) => {
      dispatch(openTextEditDialog(drawingId));
    },
    [dispatch]
  );

  return {
    addTextAnnotation,
    updateTextAnnotation,
    removeDrawing,
    clearAll,
    getDrawingsAsGeoJSON,
    editTextAnnotation,
  };
}
